import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Grid, Paper, Typography, Table, TableHead, TableRow, TableCell, TableBody, Chip, Select, MenuItem, Button, TextField, Stack, Alert } from '@mui/material';
import ConfirmationNumberRoundedIcon from '@mui/icons-material/ConfirmationNumberRounded';
import PageHeader from '../../components/common/PageHeader';
import StatCard from '../../components/common/StatCard';
import { loadTickets, createNewTicket, updateTicketRecord } from './ticketsSlice';
import { formatRelativeTime } from '../../utils/severity';
import { tokens } from '../../theme/theme';
import { fetchUsers } from '../../services/api/identityService';
import useCurrentPermissions from '../../hooks/useCurrentPermissions';

const PRIORITY_COLOR = { low: tokens.severity.low, medium: tokens.severity.medium, high: tokens.severity.high, critical: tokens.severity.critical };
const STATUS_OPTIONS = ['open', 'in_progress', 'waiting_on_client', 'resolved', 'closed'];
const SOURCE_LABEL = { analyst: 'Analyst', client_portal: 'Client Portal', automation_engine: 'Automation Engine' };

export default function TicketsPage() {
  const dispatch = useDispatch();
  const { items, status, mutationStatus, error } = useSelector((s) => s.tickets);
  const { can, loaded } = useCurrentPermissions();
  const canWrite = can('ticket:write');
  const [users, setUsers] = useState([]);
  const [title, setTitle] = useState('');

  useEffect(() => { dispatch(loadTickets()); fetchUsers().then(setUsers).catch(() => setUsers([])); }, [dispatch]);

  const open = items.filter((t) => t.status !== 'resolved' && t.status !== 'closed').length;
  const critical = items.filter((t) => t.priority === 'critical').length;
  const fromAutomation = items.filter((t) => t.source === 'automation_engine').length;

  const handleCreate = () => {
    if (!title.trim()) return;
    dispatch(createNewTicket({ title: title.trim(), priority: 'medium', source: 'analyst' })).unwrap().then(() => setTitle('')).catch(() => {});
  };

  return (
    <Box>
      <PageHeader title="Ticket Management" subtitle="Tracks work across the platform — including tickets auto-opened by the Automation & Response engine" />
      <Grid container spacing={2.5} sx={{ mb: 2.5 }}>
        <Grid item xs={12} sm={3}><StatCard label="Total Tickets" value={items.length} icon={ConfirmationNumberRoundedIcon} /></Grid>
        <Grid item xs={12} sm={3}><StatCard label="Open" value={open} accent={tokens.severity.medium} /></Grid>
        <Grid item xs={12} sm={3}><StatCard label="Critical" value={critical} accent={tokens.severity.critical} /></Grid>
        <Grid item xs={12} sm={3}><StatCard label="From Automation" value={fromAutomation} accent={tokens.severity.info} /></Grid>
      </Grid>

      {error && <Alert severity="error" sx={{mb:2}}>{error}</Alert>}
      {loaded && !canWrite && <Alert severity="info" sx={{mb:2}}>Your role has read-only ticket access. Ask an administrator for ticket:write permission to create or change tickets.</Alert>}
      <Paper sx={{ p: 2, mb: 2.5 }}>
        <Stack direction="row" spacing={1.5}>
          <TextField disabled={!canWrite} size="small" fullWidth placeholder="New ticket title" value={title} onChange={(e) => setTitle(e.target.value)} />
          <Button variant="contained" disabled={!canWrite || mutationStatus==='loading'} onClick={handleCreate}>Create</Button>
        </Stack>
      </Paper>

      <Paper sx={{ overflow: 'hidden' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              {['ID', 'Title', 'Priority', 'Source', 'Assigned To', 'Status', 'Created'].map((h) => (
                <TableCell key={h} sx={{ color: tokens.text.muted, fontSize: '0.68rem', textTransform: 'uppercase' }}>{h}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((t) => (
              <TableRow key={t.id} hover>
                <TableCell sx={{ fontFamily: '"JetBrains Mono", monospace', fontSize: '0.74rem' }}>{t.id}</TableCell>
                <TableCell sx={{ fontSize: '0.8rem', maxWidth: 280 }}>{t.title}</TableCell>
                <TableCell><Chip label={t.priority} size="small" sx={{ color: PRIORITY_COLOR[t.priority], border: `1px solid ${PRIORITY_COLOR[t.priority]}`, backgroundColor: 'transparent' }} /></TableCell>
                <TableCell sx={{ fontSize: '0.74rem', color: tokens.text.muted }}>{SOURCE_LABEL[t.source] || t.source}</TableCell>
                <TableCell>
                  <Select size="small" value={t.assignedTo || ''} variant="standard" disableUnderline displayEmpty
                    sx={{ fontSize: '0.76rem', minWidth: 130 }}
                    disabled={!canWrite}
                    onChange={(e) => e.target.value && dispatch(updateTicketRecord({ id: t.id, changes: { assignedToUserId: e.target.value } }))}>
                    <MenuItem value="" disabled sx={{ fontSize: '0.78rem' }}>Unassigned</MenuItem>
                    {users.filter((u) => String(u.status || '').toLowerCase() === 'active').map((u) => (
                      <MenuItem key={u.id} value={u.id} sx={{ fontSize: '0.78rem' }}>{u.fullName || u.name || u.email}</MenuItem>
                    ))}
                  </Select>
                </TableCell>
                <TableCell>
                  <Select
                    disabled={!canWrite}
                    size="small" value={t.status} variant="standard" disableUnderline
                    sx={{ fontSize: '0.76rem' }}
                    onChange={(e) => dispatch(updateTicketRecord({ id: t.id, changes: { status: e.target.value } }))}
                  >
                    {STATUS_OPTIONS.map((s) => <MenuItem key={s} value={s} sx={{ fontSize: '0.78rem' }}>{s.replace('_', ' ')}</MenuItem>)}
                  </Select>
                </TableCell>
                <TableCell sx={{ fontSize: '0.7rem', color: tokens.text.muted, whiteSpace: 'nowrap' }}>{formatRelativeTime(t.createdAt)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>
    </Box>
  );
}
